import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow, format, parseISO } from "date-fns";
import { enUS, es, fr, de } from "date-fns/locale";

// Map locale codes to date-fns locales
const LOCALE_MAP: Record<string, Locale> = {
  en: enUS,
  es: es,
  fr: fr,
  de: de,
};

export const useLocalization = () => {
  const { user } = useAuth();
  const [timezone, setTimezone] = useState<string>("UTC");
  const [language, setLanguage] = useState<string>("en");
  const [currency, setCurrency] = useState<string>("usd");

  useEffect(() => {
    if (!user) return;

    // Load initial localization preferences
    const loadPreferences = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("timezone, language, currency")
        .eq("id", user.id)
        .single();

      if (data) {
        setTimezone(data.timezone || "UTC");
        setLanguage(data.language || "en");
        setCurrency(data.currency || "usd");
      }
    };

    loadPreferences();
    
    // Note: Real-time updates are now handled by StoreProvider
    // to avoid duplicate subscriptions
  }, [user]);

  // Format date with user's timezone and language
  const formatDate = (dateString: string | null | undefined, options: Intl.DateTimeFormatOptions = {}): string => {
    if (!dateString) return "N/A";
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      
      // Use user's timezone for formatting
      const formatter = new Intl.DateTimeFormat(language, {
        timeZone: timezone,
        ...options,
      });
      
      return formatter.format(date);
    } catch {
      return "N/A";
    }
  };

  // Format distance to now with user's language
  const formatDistance = (dateString: string | null | undefined): string => {
    if (!dateString) return "N/A";
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      
      const locale = LOCALE_MAP[language] || enUS;
      return formatDistanceToNow(date, { addSuffix: true, locale });
    } catch {
      return "N/A";
    }
  };

  // Format currency with user's selected currency
  const formatCurrency = (cents: number): string => {
    // Map currency codes to display symbols
    const currencySymbols: Record<string, string> = {
      usd: "$",
      eur: "€",
      gbp: "£",
    };
    
    const symbol = currencySymbols[currency] || "$";
    
    if (cents == null || !Number.isFinite(cents)) return `${symbol}0.00`;
    const amount = cents / 100;
    
    return `${symbol}${amount.toFixed(2)}`;
  };

  return {
    timezone,
    language,
    currency,
    formatDate,
    formatDistance,
    formatCurrency,
  };
};